/** Typed wrappers around every API endpoint. */
import { api, setTokens, clearTokens } from './client'
import type {
  AccountGroupNode, DayBook, Ledger, LedgerBalances, LedgerStatement, Me, PartyCreate,
  Register, TripTotals,
  TokenResponse, TrialBalance, Voucher, VoucherCreate,
} from './types'

const V1 = '/api/v1'

// --- auth -------------------------------------------------------------

export async function login(
  username: string, password: string, companyId?: number,
): Promise<TokenResponse> {
  const { data } = await api.post<TokenResponse>(`${V1}/auth/login`, {
    username, password, company_id: companyId ?? null,
  })
  setTokens(data.access_token, data.refresh_token)
  return data
}

/**
 * Move this session to another company.
 *
 * A new signed token, because the company is inside it. Note what is NOT
 * here: the password. Switching used to call login() again with a
 * password the browser held in memory for the life of the session, which
 * also meant switching did nothing at all after a page refresh.
 */
export async function switchCompanyApi(
  companyId: number,
): Promise<TokenResponse> {
  const { data } = await api.post<TokenResponse>(
    `${V1}/auth/switch-company`, { company_id: companyId })
  setTokens(data.access_token, data.refresh_token)
  return data
}

export function logout() {
  clearTokens()
}

export async function fetchMe(): Promise<Me> {
  const { data } = await api.get<Me>(`${V1}/auth/me`)
  return data
}

// --- masters ----------------------------------------------------------

export async function fetchLedgers(includeInactive = false): Promise<Ledger[]> {
  const { data } = await api.get<Ledger[]>(`${V1}/ledgers`, {
    params: { include_inactive: includeInactive },
  })
  return data
}

export async function searchLedgers(q: string, limit = 20): Promise<Ledger[]> {
  const { data } = await api.get<Ledger[]>(`${V1}/ledgers/search`, {
    params: { q, limit },
  })
  return data
}

export async function fetchAccountTree(): Promise<AccountGroupNode[]> {
  const { data } = await api.get<AccountGroupNode[]>(`${V1}/accounts/tree`)
  return data
}

export async function createParty(body: PartyCreate): Promise<Ledger> {
  const { data } = await api.post<Ledger>(`${V1}/parties`, body)
  return data
}

// --- vouchers ---------------------------------------------------------

export async function postVoucher(body: VoucherCreate): Promise<Voucher> {
  const { data } = await api.post<Voucher>(`${V1}/vouchers`, body)
  return data
}

export async function fetchVoucher(id: number): Promise<Voucher> {
  const { data } = await api.get<Voucher>(`${V1}/vouchers/${id}`)
  return data
}

export async function reverseVoucher(id: number, reason: string): Promise<Voucher> {
  const { data } = await api.post<Voucher>(`${V1}/vouchers/${id}/reverse`, { reason })
  return data
}

// --- reports ----------------------------------------------------------

interface Range { from_date?: string; to_date?: string }

export async function fetchDayBook(
  params: Range & { kind?: string } = {},
): Promise<DayBook> {
  const { data } = await api.get<DayBook>(`${V1}/reports/daybook`, { params })
  return data
}

export async function fetchRegister(
  params: Range & { kind: 'sales' | 'purchase' },
): Promise<Register> {
  const { data } = await api.get<Register>(`${V1}/reports/register`, { params })
  return data
}

export async function fetchLedgerStatement(
  code: string, params: Range = {},
): Promise<LedgerStatement> {
  const { data } = await api.get<LedgerStatement>(
    `${V1}/reports/ledger/${encodeURIComponent(code)}`, { params })
  return data
}

/**
 * Today's balance of every account this user may look at. Fetched once
 * when voucher entry opens and again after each save -- never per
 * keystroke.
 */
export async function fetchLedgerBalances(): Promise<LedgerBalances> {
  const { data } = await api.get<LedgerBalances>(`${V1}/reports/balances`)
  return data
}

export async function fetchTrialBalance(params: Range = {}): Promise<TrialBalance> {
  const { data } = await api.get<TrialBalance>(`${V1}/reports/trial-balance`, { params })
  return data
}

export async function fetchProfitLoss(params: Range = {}) {
  const { data } = await api.get(`${V1}/reports/profit-loss`, { params })
  return data
}

export async function fetchBalanceSheet(params: { as_of?: string; fy_start?: string } = {}) {
  const { data } = await api.get(`${V1}/reports/balance-sheet`, { params })
  return data
}

// --- chart of accounts admin ------------------------------------------

import type {
  AccountGroupFlat, CodePolicy, CompanyCreate, CompanyInfo, CompanyRow,
  FinancialYear, GroupStyle,
  ImportResult, LedgerFull, RenumberResult, RoleTemplate, StaffUser,
} from './types'

export async function fetchPolicy(): Promise<CodePolicy> {
  const { data } = await api.get<CodePolicy>(`${V1}/chart/policy`)
  return data
}

export async function updatePolicy(body: Partial<CodePolicy>): Promise<CodePolicy> {
  const { data } = await api.patch<CodePolicy>(`${V1}/chart/policy`, body)
  return data
}

export async function renumberGroups(
  group_style: GroupStyle, dry_run: boolean,
): Promise<RenumberResult> {
  const { data } = await api.post<RenumberResult>(`${V1}/chart/renumber`,
    { group_style, dry_run })
  return data
}

export async function fetchGroups(): Promise<AccountGroupFlat[]> {
  const { data } = await api.get<AccountGroupFlat[]>(`${V1}/chart/groups`)
  return data
}

export async function createGroup(body: {
  name_en: string; nature: string; parent_code?: string | null
  name_ar?: string; system_key?: string; affects_gross_profit?: boolean
}): Promise<AccountGroupFlat> {
  const { data } = await api.post<AccountGroupFlat>(`${V1}/chart/groups`, body)
  return data
}

export async function updateGroup(code: string, body: {
  name_en?: string; name_ar?: string | null
  parent_code?: string | null; affects_gross_profit?: boolean
}): Promise<AccountGroupFlat> {
  const { data } = await api.patch<AccountGroupFlat>(
    `${V1}/chart/groups/${encodeURIComponent(code)}`, body)
  return data
}

export async function deleteGroup(code: string): Promise<void> {
  await api.delete(`${V1}/chart/groups/${encodeURIComponent(code)}`)
}

export async function fetchLedgerFull(code: string): Promise<LedgerFull> {
  const { data } = await api.get<LedgerFull>(
    `${V1}/chart/ledgers/${encodeURIComponent(code)}`)
  return data
}

export async function createLedgerFull(body: Record<string, unknown>): Promise<LedgerFull> {
  const { data } = await api.post<LedgerFull>(`${V1}/chart/ledgers`, body)
  return data
}

export async function updateLedger(
  code: string, body: Record<string, unknown>,
): Promise<LedgerFull> {
  const { data } = await api.patch<LedgerFull>(
    `${V1}/chart/ledgers/${encodeURIComponent(code)}`, body)
  return data
}

export async function deleteLedger(code: string): Promise<void> {
  await api.delete(`${V1}/chart/ledgers/${encodeURIComponent(code)}`)
}

export async function importLedgers(
  file: File, dryRun: boolean,
): Promise<ImportResult> {
  const form = new FormData()
  form.append('file', file)
  const { data } = await api.post<ImportResult>(
    `${V1}/chart/import?dry_run=${dryRun}`, form,
    { headers: { 'Content-Type': 'multipart/form-data' } })
  return data
}

// --- users -------------------------------------------------------------

export async function fetchStaff(): Promise<StaffUser[]> {
  const { data } = await api.get<StaffUser[]>(`${V1}/admin/users`)
  return data
}

export async function fetchRoleTemplates(): Promise<RoleTemplate[]> {
  const { data } = await api.get<RoleTemplate[]>(`${V1}/admin/roles`)
  return data
}

export async function fetchCompanies(): Promise<CompanyRow[]> {
  const { data } = await api.get<CompanyRow[]>(`${V1}/company/list`)
  return data
}

export async function createCompany(body: CompanyCreate): Promise<CompanyRow> {
  const { data } = await api.post<CompanyRow>(`${V1}/company`, body)
  return data
}

export async function createStaff(body: Record<string, unknown>): Promise<StaffUser> {
  const { data } = await api.post<StaffUser>(`${V1}/admin/users`, body)
  return data
}

export async function updateStaff(
  id: number, body: Record<string, unknown>,
): Promise<StaffUser> {
  const { data } = await api.patch<StaffUser>(`${V1}/admin/users/${id}`, body)
  return data
}

// --- company -----------------------------------------------------------

export async function fetchCompany(): Promise<CompanyInfo> {
  const { data } = await api.get<CompanyInfo>(`${V1}/company/current`)
  return data
}

export async function updateCompany(body: Record<string, unknown>): Promise<CompanyInfo> {
  const { data } = await api.patch<CompanyInfo>(`${V1}/company/current`, body)
  return data
}

export async function fetchYears(): Promise<FinancialYear[]> {
  const { data } = await api.get<FinancialYear[]>(`${V1}/company/years`)
  return data
}

export async function lockYear(year: number, lock: boolean): Promise<FinancialYear> {
  const { data } = await api.post<FinancialYear>(
    `${V1}/company/years/${year}/${lock ? 'lock' : 'unlock'}`)
  return data
}

// --- outstanding -------------------------------------------------------

import type { OutstandingReport, PartyBills } from './types'

export async function fetchOutstanding(params: {
  kind?: 'receivable' | 'payable'; as_of?: string; include_bills?: boolean
} = {}): Promise<OutstandingReport> {
  const { data } = await api.get<OutstandingReport>(`${V1}/outstanding`, { params })
  return data
}

export async function fetchPartyBills(
  code: string, params: { as_of?: string } = {},
): Promise<PartyBills> {
  const { data } = await api.get<PartyBills>(
    `${V1}/outstanding/party/${encodeURIComponent(code)}`, { params })
  return data
}

// --- opening balances --------------------------------------------------

import type { LegacyImportResult, OpeningState } from './types'

export async function fetchOpening(): Promise<OpeningState> {
  const { data } = await api.get<OpeningState>(`${V1}/opening`)
  return data
}

export async function saveOpening(
  lines: { ledger_code: string; amount: string; dr_cr: 'D' | 'C' }[],
  balanceToSuspense = false,
) {
  const { data } = await api.put(`${V1}/opening`,
    { lines, balance_to_suspense: balanceToSuspense })
  return data
}

export async function importLegacyOpening(dryRun: boolean): Promise<LegacyImportResult> {
  const { data } = await api.post<LegacyImportResult>(
    `${V1}/opening/import-legacy?dry_run=${dryRun}`)
  return data
}

// --- PDF export --------------------------------------------------------

/**
 * Download a report as PDF.
 *
 * Rendered server-side: browsers do not implement CSS paged media, so
 * page numbers, repeating column headers and running headers are only
 * possible off the client.
 */
export async function downloadReportPdf(
  path: string,
  params: Record<string, string | undefined> = {},
  filename = 'report.pdf',
): Promise<void> {
  // A path starting with / is under /api/v1 as it stands (the inventory
  // reports live under /inventory/reports); anything else is under /reports.
  const url0 = path.startsWith('/') ? `${V1}${path}` : `${V1}/reports/${path}`
  const { data } = await api.get<Blob>(url0, {
    params, responseType: 'blob',
  })
  const url = URL.createObjectURL(data)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

// --- server capabilities -----------------------------------------------

export interface Capabilities { pdf: boolean; pdf_reason: string | null }

export async function fetchCapabilities(): Promise<Capabilities> {
  const { data } = await api.get<Capabilities>(`${V1}/capabilities`)
  return data
}

// --- transport ---------------------------------------------------------

import type {
  Driver, InvoicePreview, TransportRoute, Trip, TripCorrection, TripIn,
  TripInvoice, Vehicle,
} from './types'

const TR = `${V1}/transport`

export async function fetchVehicles(includeInactive = false): Promise<Vehicle[]> {
  const { data } = await api.get<Vehicle[]>(`${TR}/vehicles`,
    { params: { include_inactive: includeInactive } })
  return data
}

export async function createVehicle(body: {
  plate_no: string; description?: string | null; kind: string
  subcontractor_code?: string | null
}): Promise<Vehicle> {
  const { data } = await api.post<Vehicle>(`${TR}/vehicles`, body)
  return data
}

export async function setVehicleActive(id: number, active: boolean) {
  const { data } = await api.post<Vehicle>(`${TR}/vehicles/${id}/active`,
    { active })
  return data
}

export async function fetchDrivers(includeInactive = false): Promise<Driver[]> {
  const { data } = await api.get<Driver[]>(`${TR}/drivers`,
    { params: { include_inactive: includeInactive } })
  return data
}

export async function createDriver(body: {
  name: string; phone?: string | null
}): Promise<Driver> {
  const { data } = await api.post<Driver>(`${TR}/drivers`, body)
  return data
}

export async function fetchRoutes(includeInactive = false)
: Promise<TransportRoute[]> {
  const { data } = await api.get<TransportRoute[]>(`${TR}/routes`,
    { params: { include_inactive: includeInactive } })
  return data
}

export async function createRoute(body: {
  from_place: string; to_place: string
  default_rate?: string | null; income_ledger_code?: string | null
}): Promise<TransportRoute> {
  const { data } = await api.post<TransportRoute>(`${TR}/routes`, body)
  return data
}

export async function setCustomerRate(body: {
  customer_code: string; route_id: number; rate: string
}) {
  const { data } = await api.post(`${TR}/rates`, body)
  return data
}

/** The rate to pre-fill. Only ever a suggestion; the clerk overtypes it. */
export async function fetchRate(customerCode: string, routeId: number)
: Promise<{ rate: string | null }> {
  const { data } = await api.get<{ rate: string | null }>(`${TR}/rates`,
    { params: { customer_code: customerCode, route_id: routeId } })
  return data
}

export async function fetchTrips(params: {
  from_date?: string; to_date?: string; customer_code?: string
  vehicle_plate?: string; status?: string; unbilled_only?: boolean
} = {}): Promise<{ count: number; trips: Trip[]; totals?: TripTotals }> {
  const { data } = await api.get(`${TR}/trips`, { params })
  return data
}

export async function addTrips(trips: TripIn[])
: Promise<{ count: number; trips: Trip[] }> {
  const { data } = await api.post(`${TR}/trips`, { trips })
  return data
}

export async function updateTrip(id: number, body: Partial<TripIn>)
: Promise<Trip> {
  const { data } = await api.patch<Trip>(`${TR}/trips/${id}`, body)
  return data
}

export async function previewInvoice(body: {
  customer_code: string; trip_ids: number[]
}): Promise<InvoicePreview> {
  const { data } = await api.post<InvoicePreview>(`${TR}/invoices/preview`, body)
  return data
}

export async function createTripInvoice(body: {
  customer_code: string; trip_ids: number[]
  invoice_date?: string; from_date?: string; to_date?: string
}): Promise<TripInvoice> {
  const { data } = await api.post<TripInvoice>(`${TR}/invoices`, body)
  return data
}

export async function fetchTripInvoices(params: {
  customer_code?: string; from_date?: string; to_date?: string
} = {}): Promise<{ count: number; invoices: TripInvoice[] }> {
  const { data } = await api.get(`${TR}/invoices`, { params })
  return data
}

export async function fetchTripInvoice(id: number): Promise<TripInvoice> {
  const { data } = await api.get<TripInvoice>(`${TR}/invoices/${id}`)
  return data
}

export async function cancelTripInvoice(id: number, reason: string) {
  const { data } = await api.post<TripInvoice>(`${TR}/invoices/${id}/cancel`,
    { reason })
  return data
}

export async function correctTripInvoice(id: number, body: {
  trips: TripCorrection[]; reason?: string; note_date?: string
}): Promise<TripInvoice> {
  const { data } = await api.post<TripInvoice>(
    `${TR}/invoices/${id}/corrections`, body)
  return data
}

export async function downloadInvoiceNotePdf(
  note: { id: number; kind: string; note_no: string },
): Promise<void> {
  await downloadReportPdf(
    `invoice-note/${note.id}/pdf`, {},
    `${note.kind.toLowerCase()}-note-${note.note_no.replace(/\//g, '-')}.pdf`)
}

/**
 * Download the invoice PDF.
 *
 * Goes through the authenticated client rather than putting a URL in an
 * <a href>: the endpoint needs the bearer token, and a plain link sends
 * no Authorization header, so the "download" would be a 401 page.
 */
export async function downloadTripInvoicePdf(
  invoice: { id: number; invoice_no: string }, lang = 'en',
): Promise<void> {
  await downloadReportPdf(
    `trip-invoice/${invoice.id}/pdf`, { lang },
    `invoice-${invoice.invoice_no.replace(/\//g, '-')}.pdf`)
}


// --- phase 1 reports ------------------------------------------------------

import type {
  AuditEntry, CashBook, Profitability, VatReturn,
} from './types'

export async function fetchCashBook(params: {
  kind: 'cash' | 'bank'; account?: string; from_date?: string; to_date?: string
}): Promise<CashBook> {
  const { data } = await api.get<CashBook>(`${V1}/reports/cash-book`, { params })
  return data
}

export async function fetchVatReturn(params: {
  year?: number; quarter?: number; from_date?: string; to_date?: string
}): Promise<VatReturn> {
  const { data } = await api.get<VatReturn>(`${V1}/reports/vat-return`, { params })
  return data
}

export async function fetchProfitability(params: {
  from_date?: string; to_date?: string
}): Promise<Profitability> {
  const { data } = await api.get<Profitability>(
    `${V1}/transport/profitability`, { params })
  return data
}

export async function setVehicleCostGroup(id: number, groupCode: string | null) {
  const { data } = await api.post(
    `${V1}/transport/vehicles/${id}/cost-group`, { group_code: groupCode })
  return data
}

export async function fetchAudit(params: {
  action?: string; actor?: string; from_date?: string; to_date?: string
  limit?: number; offset?: number
}): Promise<{ total: number; limit: number; offset: number
              entries: AuditEntry[] }> {
  const { data } = await api.get(`${V1}/audit`, { params })
  return data
}

export async function fetchAuditActions(): Promise<string[]> {
  const { data } = await api.get<string[]>(`${V1}/audit/actions`)
  return data
}

// --- inventory (5.1) -----------------------------------------------------

import type {
  InvGroup, InvImportResult, InvItem, InvItemIn, InvSalesman, InvStore,
  OpeningStock, OpeningStockLine,
} from './types'

const INV = `${V1}/inventory`

export async function fetchInvItems(params: {
  q?: string; group_id?: number; active?: boolean
} = {}): Promise<{ count: number; items: InvItem[]; stock_value?: string }> {
  const { data } = await api.get(`${INV}/items`, { params })
  return data
}

export async function saveInvItem(body: InvItemIn, id?: number): Promise<InvItem> {
  const { data } = id
    ? await api.put<InvItem>(`${INV}/items/${id}`, body)
    : await api.post<InvItem>(`${INV}/items`, body)
  return data
}

export async function fetchInvGroups(): Promise<InvGroup[]> {
  const { data } = await api.get<InvGroup[]>(`${INV}/groups`)
  return data
}

export async function saveInvGroup(body: { name_en: string; name_ar?: string | null
  is_active?: boolean }, id?: number): Promise<InvGroup> {
  const { data } = id
    ? await api.put<InvGroup>(`${INV}/groups/${id}`, { is_active: true, ...body })
    : await api.post<InvGroup>(`${INV}/groups`, { is_active: true, ...body })
  return data
}

export async function fetchStores(): Promise<InvStore[]> {
  const { data } = await api.get<InvStore[]>(`${INV}/stores`)
  return data
}

export async function saveStore(body: Omit<InvStore, 'id' | 'uid' | 'item_count'
  | 'stock_value'>, id?: number): Promise<InvStore> {
  const { data } = id
    ? await api.put<InvStore>(`${INV}/stores/${id}`, body)
    : await api.post<InvStore>(`${INV}/stores`, body)
  return data
}

export async function fetchSalesmen(): Promise<InvSalesman[]> {
  const { data } = await api.get<InvSalesman[]>(`${INV}/salesmen`)
  return data
}

export async function fetchOpeningStock(storeId: number): Promise<OpeningStock> {
  const { data } = await api.get<OpeningStock>(`${INV}/opening/${storeId}`)
  return data
}

export async function saveOpeningStock(storeId: number,
  lines: Pick<OpeningStockLine, 'item_code' | 'qty' | 'unit' | 'unit_cost'>[])
: Promise<OpeningStock> {
  const { data } = await api.put<OpeningStock>(`${INV}/opening/${storeId}`, { lines })
  return data
}

export async function postOpeningStockToBooks()
: Promise<{ total_value: string; books_value: string }> {
  const { data } = await api.post(`${INV}/opening/post-to-books`)
  return data
}

async function upload(url: string, file: File, dryRun: boolean): Promise<InvImportResult> {
  const form = new FormData()
  form.append('file', file)
  const { data } = await api.post<InvImportResult>(`${url}?dry_run=${dryRun}`, form,
    { headers: { 'Content-Type': 'multipart/form-data' } })
  return data
}

export const importInvItems = (file: File, dryRun: boolean) =>
  upload(`${INV}/items/import`, file, dryRun)

export const importOpeningStock = (storeId: number, file: File, dryRun: boolean) =>
  upload(`${INV}/opening/${storeId}/import`, file, dryRun)

// --- purchases (5.2) -------------------------------------------------------

import type { LastCost, PurchaseDetail, PurchaseIn, PurchaseRow } from './types'

export async function fetchPurchases(params: {
  from_date?: string; to_date?: string; supplier_code?: string; kind?: string
} = {}): Promise<{ count: number; purchases: PurchaseRow[]
                   totals: { subtotal: string; vat_amount: string; total: string } }> {
  const { data } = await api.get(`${INV}/purchases`, { params })
  return data
}

export async function fetchPurchase(id: number): Promise<PurchaseDetail> {
  const { data } = await api.get<PurchaseDetail>(`${INV}/purchases/${id}`)
  return data
}

export async function createPurchase(body: PurchaseIn): Promise<PurchaseDetail> {
  const { data } = await api.post<PurchaseDetail>(`${INV}/purchases`, body)
  return data
}

export async function checkPurchaseDuplicate(supplierCode: string, no: string)
: Promise<{ id: number; doc_no: string; doc_date: string; total: string } | null> {
  const { data } = await api.get(`${INV}/purchases/duplicate`,
    { params: { supplier_code: supplierCode, supplier_invoice_no: no } })
  return data.duplicate
}

export async function fetchLastCost(itemCode: string, supplierCode?: string)
: Promise<LastCost | null> {
  const { data } = await api.get(`${INV}/last-cost`,
    { params: { item_code: itemCode, supplier_code: supplierCode || undefined } })
  return data.last
}

export async function returnPurchase(id: number, body: {
  doc_date: string; notes?: string | null; lines: { line_id: number; qty: string }[]
}): Promise<PurchaseDetail> {
  const { data } = await api.post<PurchaseDetail>(`${INV}/purchases/${id}/return`, body)
  return data
}

export async function cancelPurchase(id: number, reason: string): Promise<PurchaseDetail> {
  const { data } = await api.post<PurchaseDetail>(`${INV}/purchases/${id}/cancel`, { reason })
  return data
}

export async function fetchInvItem(id: number): Promise<InvItem & {
  last_purchase?: { date: string; doc_no: string; supplier_name: string; unit: string
                    unit_cost: string } | null }> {
  const { data } = await api.get(`${INV}/items/${id}`)
  return data
}

// --- sales (5.3) -----------------------------------------------------------

import type { SaleDetail, SaleIn, SaleRow } from './types'

export async function fetchSales(params: {
  from_date?: string; to_date?: string; customer_code?: string; kind?: string
  store_id?: number
} = {}): Promise<{ count: number; sales: SaleRow[]
                   totals: { subtotal: string; vat_amount: string; total: string
                             cost?: string; gross_profit?: string } }> {
  const { data } = await api.get(`${INV}/sales`, { params })
  return data
}

export async function fetchSale(id: number): Promise<SaleDetail> {
  const { data } = await api.get<SaleDetail>(`${INV}/sales/${id}`)
  return data
}

export async function createSale(body: SaleIn): Promise<SaleDetail> {
  const { data } = await api.post<SaleDetail>(`${INV}/sales`, body)
  return data
}

export async function returnSale(id: number, body: {
  doc_date: string; notes?: string | null; lines: { line_id: number; qty: string }[]
}): Promise<SaleDetail> {
  const { data } = await api.post<SaleDetail>(`${INV}/sales/${id}/return`, body)
  return data
}

export async function cancelSale(id: number, reason: string): Promise<SaleDetail> {
  const { data } = await api.post<SaleDetail>(`${INV}/sales/${id}/cancel`, { reason })
  return data
}

/** The A4 tax invoice, downloaded as a PDF. */
export async function downloadSalePdf(id: number, filename: string): Promise<void> {
  const { data } = await api.get<Blob>(`${INV}/sales/${id}/pdf`, { responseType: 'blob' })
  const url = URL.createObjectURL(data)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}


// --- inventory 5.4 --------------------------------------------------------

import type {
  AdjustmentDetail, AdjustmentIn, AdjustmentRow, InventorySummary, LowStockRow,
  SalesByItem, StockCard, StockReport, TransferDetail, TransferIn, TransferRow, VanStock,
} from './types'

export async function fetchTransfers(params: { from_date?: string; to_date?: string
                                               store_id?: number } = {}):
Promise<{ count: number; transfers: TransferRow[] }> {
  const { data } = await api.get(`${INV}/transfers`, { params })
  return data
}

export async function fetchTransfer(id: number): Promise<TransferDetail> {
  const { data } = await api.get<TransferDetail>(`${INV}/transfers/${id}`)
  return data
}

export async function createTransfer(body: TransferIn): Promise<TransferDetail> {
  const { data } = await api.post<TransferDetail>(`${INV}/transfers`, body)
  return data
}

export async function cancelTransfer(id: number, reason: string): Promise<TransferDetail> {
  const { data } = await api.post<TransferDetail>(`${INV}/transfers/${id}/cancel`, { reason })
  return data
}

export async function fetchAdjustments(params: { from_date?: string; to_date?: string
                                                 store_id?: number } = {}):
Promise<{ count: number; adjustments: AdjustmentRow[] }> {
  const { data } = await api.get(`${INV}/adjustments`, { params })
  return data
}

export async function fetchAdjustment(id: number): Promise<AdjustmentDetail> {
  const { data } = await api.get<AdjustmentDetail>(`${INV}/adjustments/${id}`)
  return data
}

export async function createAdjustment(body: AdjustmentIn): Promise<AdjustmentDetail> {
  const { data } = await api.post<AdjustmentDetail>(`${INV}/adjustments`, body)
  return data
}

export async function cancelAdjustment(id: number, reason: string): Promise<AdjustmentDetail> {
  const { data } = await api.post<AdjustmentDetail>(`${INV}/adjustments/${id}/cancel`,
                                                    { reason })
  return data
}

export async function fetchStockReport(params: { as_of?: string; store_id?: number
                                                 group_id?: number } = {}): Promise<StockReport> {
  const { data } = await api.get<StockReport>(`${INV}/reports/stock`, { params })
  return data
}

export async function fetchStockCard(itemId: number, params: { from_date?: string
  to_date?: string; store_id?: number } = {}): Promise<StockCard> {
  const { data } = await api.get<StockCard>(`${INV}/reports/stock-card/${itemId}`, { params })
  return data
}

export async function fetchVanStock(): Promise<{ vans: VanStock[] }> {
  const { data } = await api.get(`${INV}/reports/van-stock`)
  return data
}

export async function fetchSalesByItem(params: { from_date?: string; to_date?: string
  store_id?: number; group_id?: number } = {}): Promise<SalesByItem> {
  const { data } = await api.get<SalesByItem>(`${INV}/reports/sales-by-item`, { params })
  return data
}

export async function fetchLowStock(): Promise<{ count: number; rows: LowStockRow[] }> {
  const { data } = await api.get(`${INV}/reports/low-stock`)
  return data
}

export async function fetchInventorySummary(): Promise<InventorySummary> {
  const { data } = await api.get<InventorySummary>(`${INV}/reports/summary`)
  return data
}


// --- route sales (5.5): the office's view of the phones -------------------

import type { VanPhone, VanSyncDoc } from './types'

export async function fetchVanPhones(): Promise<{ devices: VanPhone[]; max_offline_days: number }> {
  const { data } = await api.get(`${V1}/van/devices`)
  return data
}

export async function revokeVanPhone(id: number): Promise<VanPhone> {
  const { data } = await api.post<VanPhone>(`${V1}/van/devices/${id}/revoke`)
  return data
}

export async function fetchVanDocs(params: { status?: string; store_id?: number } = {}):
Promise<{ documents: VanSyncDoc[]; failed: number }> {
  const { data } = await api.get(`${V1}/van/docs`, { params })
  return data
}

export async function retryVanDoc(id: number): Promise<VanSyncDoc> {
  const { data } = await api.post<VanSyncDoc>(`${V1}/van/docs/${id}/retry`)
  return data
}
